import React, { useRef, useState } from "react";
import { Upload, Trash2, Sparkles, Loader2, GripVertical, RefreshCw, AlertCircle, Smartphone, Check, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import AuthImg from "./AuthImg";
import PhotoLightbox from "./PhotoLightbox";
import PhotoBridgeModal from "./PhotoBridgeModal";
import { useAuth } from "@/lib/auth";
import { compressImage } from "@/lib/imageCompression";

// 3 gleichzeitige Uploads: schonend für die Storage-API und den
// Browser-Connection-Pool (max. 6 HTTP-Verbindungen pro Origin) — lässt
// Heartbeat/Auto-Save-Anfragen weiterhin durchgehen. Erlaubt zuverlässige
// Burst-Uploads von 50+ Bildern.
// Anzahl Bilder pro HTTP-Request (Backend akzeptiert UploadFile-Liste).
// Bei 50 Bildern → 5 Requests statt 50 einzelnen → spart 45× TCP/TLS-Setup.
const FILES_PER_REQUEST = 10;
// Wie viele Requests gleichzeitig laufen. Browser-Limit pro Host = 6,
// wir lassen 1 frei für Heartbeat/Auto-Save.
const PARALLEL_REQUESTS = 5;
// Wie viele Bilder gleichzeitig komprimiert werden (CPU-bound). Mehr als
// hardwareConcurrency macht keinen Sinn — wäre nur Thrashing.
const PARALLEL_COMPRESSION = Math.min(
  typeof navigator !== "undefined" ? (navigator.hardwareConcurrency || 4) : 4,
  8
);
// Pro Request max. 120 s warten — bei mobilem Edge oder HEIC nötig
const UPLOAD_TIMEOUT_MS = 120_000;
// Retry-Anzahl bei Netzfehlern / 5xx (nicht bei 4xx)
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 800;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const isRetryable = (e) => {
  const status = e?.response?.status;
  if (!status) return true;          // Netzfehler / Timeout
  if (status >= 500) return true;    // Server-Fehler
  if (status === 429) return true;   // Rate-Limit
  return false;                      // 4xx → kein Retry
};
const errorReason = (e) => {
  const status = e?.response?.status;
  if (status === 401) return "Token abgelaufen — bitte neu anmelden";
  if (status === 413) return "Datei zu groß";
  if (status === 415) return "Format nicht unterstützt";
  if (!status) return e?.code === "ECONNABORTED" ? "Timeout — zu langsame Verbindung" : "Netzfehler";
  if (status >= 500) return "Server-Fehler — bitte erneut versuchen";
  return apiError(e);
};

export default function PhotoUploader({ caseId, photos = [], onChange }) {
  const { accessToken } = useAuth();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [bridgeOpen, setBridgeOpen] = useState(false);
  // Optimistic Placeholders — { tempId, previewUrl, status, name, file, error? }
  const [pending, setPending] = useState([]);
  const photosRef = useRef(photos);
  photosRef.current = photos;
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Eigentlicher Upload eines Batch (mehrere komprimierte Files in EINEM Request)
  // → spart pro Batch (FILES_PER_REQUEST - 1) HTTP-Handshakes.
  const uploadBatchOnce = async (compressedFiles) => {
    const fd = new FormData();
    compressedFiles.forEach((cf) => {
      fd.append("files", cf.compressed, cf.name);
    });
    const { data } = await api.post(`/files/cases/${caseId}/photos`, fd, {
      timeout: UPLOAD_TIMEOUT_MS,
    });
    return data?.photos || [];
  };

  // Komprimieren eines einzelnen Files (mit Fallback).
  const compressOne = async (file) => {
    try {
      const c = await compressImage(file);
      return { compressed: c, name: c.name || file.name };
    } catch {
      return { compressed: file, name: file.name };
    }
  };

  // Worker-Pool: max PARALLEL_COMPRESSION CPU-Slots gleichzeitig.
  // Resultate streamen in Reihenfolge zurück.
  const compressAllInPool = async (files, onItemDone) => {
    const compressedSlots = new Array(files.length);
    let cursor = 0;
    async function worker() {
      while (cursor < files.length) {
        const myIdx = cursor++;
        const result = await compressOne(files[myIdx]);
        compressedSlots[myIdx] = result;
        onItemDone?.(myIdx, result);
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(PARALLEL_COMPRESSION, files.length) }, () => worker())
    );
    return compressedSlots;
  };

  // Upload eines Batch mit Retry. Gibt Liste der gespeicherten Photo-Objekte zurück.
  const uploadBatchWithRetry = async (batch) => {
    let lastErr;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        return await uploadBatchOnce(batch);
      } catch (e) {
        lastErr = e;
        if (!isRetryable(e) || attempt === MAX_RETRIES) throw e;
        await sleep(RETRY_DELAY_MS * (attempt + 1));
      }
    }
    throw lastErr;
  };

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;
    const files = Array.from(fileList);

    // 1. Sofortige Vorschau-Placeholders (blob URLs synchron erzeugt).
    //    Werden im UI sofort sichtbar — User sieht Fortschritt visuell.
    const placeholders = files.map((f) => ({
      tempId: `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      previewUrl: URL.createObjectURL(f),
      name: f.name,
      file: f,
      status: "compressing",
    }));
    setPending((prev) => [...prev, ...placeholders]);

    // 2. Compression-Phase: alle Files parallel komprimieren (CPU-Pool).
    //    Update Placeholder-Status zu "uploading" sobald ein File fertig ist.
    const compressed = await compressAllInPool(files, (idx) => {
      const ph = placeholders[idx];
      setPending((prev) => prev.map((p) =>
        p.tempId === ph.tempId ? { ...p, status: "uploading" } : p
      ));
    });

    // 3. Upload-Phase: Files in Batches packen, max PARALLEL_REQUESTS gleichzeitig.
    //    Pro Batch: ein einziger HTTP-Request mit FILES_PER_REQUEST Files.
    const batches = [];
    for (let i = 0; i < compressed.length; i += FILES_PER_REQUEST) {
      batches.push({
        compressed: compressed.slice(i, i + FILES_PER_REQUEST),
        placeholders: placeholders.slice(i, i + FILES_PER_REQUEST),
      });
    }

    let okCount = 0;
    let failCount = 0;

    // Concurrency-Pool für Upload-Requests
    let batchCursor = 0;
    async function batchWorker() {
      while (batchCursor < batches.length) {
        const myIdx = batchCursor++;
        const batch = batches[myIdx];
        try {
          const photos = await uploadBatchWithRetry(batch.compressed);
          // In gleicher Reihenfolge in den State + onChange anhängen
          photosRef.current = [...photosRef.current, ...photos];
          onChange(photosRef.current);
          okCount += photos.length;
          // Placeholders dieses Batch erfolgreich abräumen
          const phIds = new Set(batch.placeholders.map((p) => p.tempId));
          setPending((prev) => prev.filter((p) => !phIds.has(p.tempId)));
          batch.placeholders.forEach((p) => URL.revokeObjectURL(p.previewUrl));
        } catch (e) {
          const reason = errorReason(e);
          failCount += batch.placeholders.length;
          const phIds = new Set(batch.placeholders.map((p) => p.tempId));
          setPending((prev) => prev.map((p) =>
            phIds.has(p.tempId) ? { ...p, status: "error", error: reason } : p
          ));
        }
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(PARALLEL_REQUESTS, batches.length) }, () => batchWorker())
    );

    if (okCount > 0) toast.success(`${okCount}/${files.length} Lichtbild${okCount === 1 ? "" : "er"} hochgeladen`);
    if (failCount > 0) {
      toast.error(`${failCount} Upload${failCount === 1 ? "" : "s"} fehlgeschlagen — auf das Bild klicken zum erneuten Versuch`);
    }
  };

  // Manueller Retry für ein einzelnes fehlgeschlagenes Bild
  const retryPending = async (placeholder) => {
    setPending((prev) => prev.map((p) =>
      p.tempId === placeholder.tempId ? { ...p, status: "uploading", error: null } : p
    ));
    try {
      const c = await compressOne(placeholder.file);
      const photos = await uploadBatchWithRetry([c]);
      if (photos.length > 0) {
        photosRef.current = [...photosRef.current, ...photos];
        onChange(photosRef.current);
      }
      setPending((prev) => prev.filter((p) => p.tempId !== placeholder.tempId));
      URL.revokeObjectURL(placeholder.previewUrl);
      toast.success("Upload erfolgreich");
    } catch (e) {
      const reason = errorReason(e);
      setPending((prev) => prev.map((p) =>
        p.tempId === placeholder.tempId ? { ...p, status: "error", error: reason } : p
      ));
      toast.error(`Upload fehlgeschlagen: ${reason}`);
    }
  };

  const dismissPending = (placeholder) => {
    URL.revokeObjectURL(placeholder.previewUrl);
    setPending((prev) => prev.filter((p) => p.tempId !== placeholder.tempId));
  };

  const deletePhoto = async (id) => {
    try {
      await api.delete(`/files/cases/${caseId}/photos/${id}`);
      onChange(photos.filter((p) => p.id !== id));
      toast.success("Lichtbild gelöscht");
    } catch (e) {
      toast.error(apiError(e, "Löschen fehlgeschlagen"));
    }
  };

  const analyze = async (photoId) => {
    setAnalyzing(photoId);
    try {
      const { data } = await api.post(`/ai/damage-detect`, { photo_id: photoId, case_id: caseId });
      onChange(photos.map((p) => (p.id === photoId ? { ...p, ai_damage_description: data.description } : p)));
      toast.success("Schaden analysiert");
    } catch (e) {
      toast.error(apiError(e, "Auto-Analyse fehlgeschlagen"));
    } finally {
      setAnalyzing(null);
    }
  };

  const reorder = async () => {
    if (dragItem.current == null || dragOverItem.current == null) return;
    const copy = [...photos];
    const [moved] = copy.splice(dragItem.current, 1);
    copy.splice(dragOverItem.current, 0, moved);
    dragItem.current = dragOverItem.current = null;
    onChange(copy);
    try {
      await api.patch(`/files/cases/${caseId}/photos/reorder`, copy.map((p) => p.id));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Fotos für die Dokumentation aus-/abwählen — nur ausgewählte landen im PDF.
  const setSelection = async (ids, value) => {
    const idSet = new Set(ids);
    const updated = photosRef.current.map((p) =>
      idSet.has(p.id) ? { ...p, in_documentation: value } : p
    );
    onChange(updated);
    try {
      await api.patch(`/files/cases/${caseId}/photos/selection`, {
        photo_ids: ids,
        in_documentation: value,
      });
    } catch (e) {
      toast.error(apiError(e, "Auswahl konnte nicht gespeichert werden"));
    }
  };
  const toggleInDoc = (p) => setSelection([p.id], p.in_documentation === false);
  const selectedCount = photos.filter((p) => p.in_documentation !== false).length;

  // Wenn die Photo-Bridge neue Fotos angeliefert hat, holen wir die Akte
  // neu vom Server damit das Grid die frisch eingetroffenen Bilder zeigt.
  const refreshPhotosFromServer = async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}`);
      const fresh = data?.case?.photos || data?.photos || [];
      onChange(fresh);
    } catch (e) {
      console.warn("[PhotoUploader] refresh nach Bridge-Upload fehlgeschlagen", e);
    }
  };

  const uploadingCount = pending.filter((p) => p.status === "uploading").length;
  const errorCount = pending.filter((p) => p.status === "error").length;

  return (
    <div className="space-y-5" data-testid="photo-uploader">
      {/* Mobile-Bridge Button — oben rechts, zugänglich auf jeder Größe */}
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={(e) => { e.stopPropagation(); setBridgeOpen(true); }}
          className="h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
          data-testid="photo-bridge-open-btn"
        >
          <Smartphone className="h-3.5 w-3.5 mr-1.5" />
          Vom Handy hochladen
        </Button>
      </div>

      <PhotoBridgeModal
        caseId={caseId}
        open={bridgeOpen}
        onClose={() => setBridgeOpen(false)}
        onPhotosArrived={refreshPhotosFromServer}
      />

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${
          dragOver ? "border-emerald-400 bg-emerald-50/50" : "border-zinc-300 bg-zinc-50 hover:border-cyan-400 hover:bg-cyan-50/40"
        }`}
        onClick={() => inputRef.current?.click()}
        data-testid="photo-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          data-testid="photo-file-input"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
        />
        <div className="flex flex-col items-center gap-3">
          {uploadingCount > 0 ? (
            <Loader2 className="h-10 w-10 text-emerald-500 animate-spin" />
          ) : (
            <div className="h-12 w-12 rounded-2xl brand-gradient-soft flex items-center justify-center">
              <Upload className="h-5 w-5 text-cyan-700" strokeWidth={1.6} />
            </div>
          )}
          <div>
            <div className="font-display font-semibold text-base tracking-tight">
              {uploadingCount > 0 ? `Wird hochgeladen · ${uploadingCount}` : "Serienfotos hochladen"}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">
              Tippen oder per Drag &amp; Drop · automatisch komprimiert · {FILES_PER_REQUEST}er-Batches · {PARALLEL_REQUESTS} parallel · Auto-Retry
            </div>
          </div>
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
            <Sparkles className="h-3 w-3 mr-1" /> Auto-Schadenserkennung
          </Badge>
        </div>
      </div>

      {errorCount > 0 && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800 flex items-center gap-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span>{errorCount} Upload{errorCount === 1 ? "" : "s"} fehlgeschlagen — auf das Bild klicken um erneut zu versuchen.</span>
        </div>
      )}

      {(photos.length > 0 || pending.length > 0) && (
        <>
          {photos.length > 0 && (
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-zinc-600" data-testid="photo-selection-count">
                <b>{selectedCount}</b> von {photos.length} Bildern in der Dokumentation
              </div>
              <div className="flex gap-1.5">
                <Button
                  type="button" variant="outline" size="sm" className="h-7 text-xs"
                  onClick={() => setSelection(photos.map((p) => p.id), true)}
                  data-testid="photo-select-all"
                >
                  Alle auswählen
                </Button>
                <Button
                  type="button" variant="outline" size="sm" className="h-7 text-xs"
                  onClick={() => setSelection(photos.map((p) => p.id), false)}
                  data-testid="photo-deselect-all"
                >
                  Alle abwählen
                </Button>
              </div>
            </div>
          )}
        <div
          className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2"
          data-testid="photo-grid"
        >
          {photos.map((p, idx) => (
            <div
              key={p.id}
              draggable
              onDragStart={() => (dragItem.current = idx)}
              onDragEnter={() => (dragOverItem.current = idx)}
              onDragEnd={reorder}
              className="group relative rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50 aspect-square"
              data-testid={`photo-card-${idx}`}
            >
              <AuthImg
                path={p.storage_path}
                token={accessToken}
                alt={p.original_filename}
                className={`w-full h-full object-cover cursor-zoom-in transition-all ${p.in_documentation === false ? "opacity-40 grayscale" : ""}`}
                onClick={() => setLightboxIndex(idx)}
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleInDoc(p); }}
                className={`absolute bottom-1 right-1 z-10 h-6 w-6 rounded-full flex items-center justify-center shadow border transition-colors ${
                  p.in_documentation === false
                    ? "bg-white/95 border-zinc-300 text-zinc-400 hover:border-cyan-400"
                    : "bg-cyan-600 border-cyan-600 text-white hover:bg-cyan-700"
                }`}
                data-testid={`photo-doc-toggle-${idx}`}
                title={p.in_documentation === false ? "In Dokumentation aufnehmen" : "Aus Dokumentation entfernen"}
              >
                {p.in_documentation === false ? <Circle className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
              </button>
              {p.in_documentation === false && (
                <div className="absolute top-1 right-1 z-10 text-[8px] font-bold uppercase tracking-wide bg-zinc-800/85 text-white px-1.5 py-0.5 rounded pointer-events-none group-hover:opacity-0 transition-opacity">
                  Nicht im Gutachten
                </div>
              )}
              <div className="absolute top-1 left-1 text-[9px] bg-white/95 border border-zinc-200 px-1 py-0.5 rounded flex items-center gap-0.5 text-zinc-500 font-mono">
                <GripVertical className="h-2.5 w-2.5" />{idx + 1}
              </div>
              {p.ai_damage_description && (
                <div className="absolute bottom-0 inset-x-0 px-1.5 py-1 bg-gradient-to-t from-black/85 to-black/0 text-[10px] text-white">
                  <div className="line-clamp-2">{p.ai_damage_description}</div>
                </div>
              )}
              <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition">
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-6 w-6 bg-white/95 hover:bg-white text-cyan-700 shadow"
                  onClick={(e) => { e.stopPropagation(); analyze(p.id); }}
                  disabled={analyzing === p.id}
                  data-testid={`photo-analyze-${idx}`}
                  title="Auto-Schadensanalyse"
                >
                  {analyzing === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                </Button>
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-6 w-6 bg-white/95 hover:bg-rose-50 text-rose-600 shadow"
                  onClick={(e) => { e.stopPropagation(); deletePhoto(p.id); }}
                  data-testid={`photo-delete-${idx}`}
                  title="Entfernen"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}

          {/* Optimistic Placeholders */}
          {pending.map((ph) => (
            <div
              key={ph.tempId}
              className={`relative rounded-lg overflow-hidden border bg-zinc-50 aspect-square ${
                ph.status === "error" ? "border-rose-300" : "border-emerald-300"
              }`}
              data-testid={`photo-pending-${ph.tempId}`}
            >
              <img
                src={ph.previewUrl}
                alt={ph.name}
                className={`w-full h-full object-cover ${ph.status === "error" ? "opacity-50 grayscale" : "opacity-70"}`}
              />
              {ph.status === "uploading" && (
                <div className="absolute inset-0 bg-white/30 flex items-center justify-center">
                  <div className="flex items-center gap-1 text-[9px] font-semibold text-emerald-800 bg-white/90 border border-emerald-200 rounded-full px-1.5 py-0.5 shadow">
                    <Loader2 className="h-2.5 w-2.5 animate-spin" />
                    Upload
                  </div>
                </div>
              )}
              {ph.status === "error" && (
                <div className="absolute inset-0 bg-white/40 flex flex-col items-center justify-center gap-1 p-1.5">
                  <div className="text-[9px] font-bold text-rose-700 text-center leading-tight">
                    {ph.error || "Fehler"}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => retryPending(ph)}
                      className="text-[9px] font-bold bg-cyan-600 hover:bg-cyan-700 text-white rounded px-1.5 py-0.5 inline-flex items-center gap-0.5"
                      data-testid={`photo-retry-${ph.tempId}`}
                      title="Erneut versuchen"
                    >
                      <RefreshCw className="h-2.5 w-2.5" /> Retry
                    </button>
                    <button
                      onClick={() => dismissPending(ph)}
                      className="text-[9px] font-bold bg-zinc-200 hover:bg-zinc-300 text-zinc-700 rounded px-1.5 py-0.5"
                      title="Verwerfen"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        </>
      )}

      {lightboxIndex !== null && photos[lightboxIndex] && (
        <PhotoLightbox
          photos={photos}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onDelete={async (photo) => {
            await api.delete(`/files/cases/${caseId}/photos/${photo.id}`);
            onChange(photos.filter((p) => p.id !== photo.id));
          }}
          onAnalyze={async (photoId) => {
            await analyze(photoId);
          }}
          analyzing={analyzing}
        />
      )}
    </div>
  );
}
